import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';

import { AuthService } from './auth.service';
import { Observable, throwError } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';
import { Broadcaster } from '../events/broadcaster.class';
import { Logger, LoggingService } from 'ionic-logging-service';
import { setPxdSubdomainFromUrl } from '../environment/environment.pxd-dev';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  public authHeaderName: string = 'Authorization';
  public authHeaderTokenPrefix: string = 'Bearer ';
  private logger: Logger;
  private currentAPI: string;

  constructor(
    private authService: AuthService,
    private broadcaster: Broadcaster,
    private loggingService: LoggingService,
  ) {
    this.logger = this.loggingService.getLogger("[AuthInterceptor]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  private isHttpInterceptorNeeded(httpRequest: HttpRequest<any>): boolean {
    if (httpRequest.url.includes('assets.printix.net')) {
      return false;
    } else {
      if (this.requestUrlHasEndpointApi(httpRequest.url)) {
        return true;
      }
      return false;
    }
  }

  private requestUrlHasEndpointApi(url: string): boolean {
    let hasApi = false;

    // Early return if the URL includes '/v1.2/find/'
    if (url.includes('/v1.2/find/')) {
      return hasApi;
    }

    // Early return for health check endpoint — no auth needed
    if (url.includes('/manage/health')) {
      return hasApi;
    }

    if (url.includes('https://api')) {
      // Determine if this is a printix.dev or sandbox.printix.dev environment host
      // Don't use regex in order to include broadest platform support.
       const pxdSubdomain = setPxdSubdomainFromUrl(url);

      if (url.includes('https://api.printix.net')) {
        if (!this.currentAPI) {
          this.currentAPI = '### PROD_API ###';
          console.log(this.currentAPI);
        }
        hasApi = true;
      } else if (url.includes('https://api.testenv.printix.net')) {
        if (!this.currentAPI) {
          this.currentAPI = '### TEST_API ###';
          console.log(this.currentAPI);
        }
        hasApi = true;
      } else if (url.includes('https://api.devenv.printix.net')) {
        if (!this.currentAPI) {
          this.currentAPI = '### DEV_API ###';
          console.log(this.currentAPI);
        }
        hasApi = true;
      } else if (url.includes('https://api.us.printix.net')) {
        if (!this.currentAPI) {
          this.currentAPI = '### US_PROD_API ###';
          console.log(this.currentAPI);
        }
        hasApi = true;
      } else if (url.includes('https://api.testenv.us.printix.net')) {
        if (!this.currentAPI) {
          this.currentAPI = '### US_TEST_API ###';
          console.log(this.currentAPI);
        }
        hasApi = true;
      } else if (url.includes('https://api.devenv.us.printix.net')) {
        if (!this.currentAPI) {
          this.currentAPI = '### US_DEV_API ###';
          console.log(this.currentAPI);
        }
        hasApi = true;
      } else if (url.includes('https://api.' + pxdSubdomain)) {
        if (!this.currentAPI) {
          this.currentAPI = '### '+ pxdSubdomain.replace('.printix.dev','').toUpperCase() + '_API ###';
          console.log(this.currentAPI);
        }
        hasApi = true;
      }
    }
    return hasApi;
  }

  private setAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        [this.authHeaderName]: `${this.authHeaderTokenPrefix}${token}`
      }
    });
  }

  intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const newHttpRequest: HttpRequest<any> = httpRequest.clone();
    this.logger.info('intercept, newHttpRequest METHOD: ' + newHttpRequest.method);
    this.logger.info('intercept, newHttpRequest URL: ' + newHttpRequest.url);

    if (this.isHttpInterceptorNeeded(newHttpRequest) && !newHttpRequest.headers.has(this.authHeaderName)) { // Authorization token for requests to our server if none exists already
      return this.handleHttpAuthInception(newHttpRequest, next);
    } else {
      return next.handle(newHttpRequest);
    }
  }

  private handleHttpAuthInception(newHttpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.authService.getValidTokenForRequest(newHttpRequest.url).pipe(
      mergeMap(accessToken => {
        // Add auth header and execute request
        const authenticatedRequest = this.setAuthHeader(newHttpRequest, accessToken);
        return this.executeAuthenticatedRequest(authenticatedRequest, next);
       // No catchError here - let HTTP request errors (like 404) flow through cleanly
      // Only token acquisition errors should be caught, and those are handled in getValidTokenForRequest
      })
    )
  }

  private executeAuthenticatedRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.logger.info('executeAuthenticatedRequest()');
    return next.handle(request).pipe(
      catchError((errorResponse: any) => {
        // Only handle auth-specific errors here
        if (errorResponse.status === 401 || errorResponse.status === 403) {
          this.logger.info('Auth error detected, attempting token refresh');
          return this.handleAuthErrorWithRefresh(request, next, errorResponse);
        }

        // For all other errors (including 404), let them propagate cleanly
        throw errorResponse;
      })
    );
  }

  private handleAuthErrorWithRefresh(request: HttpRequest<any>, next: HttpHandler, originalError: any): Observable<HttpEvent<any>> {
    this.logger.info('handleAuthErrorWithRefresh()');
    return this.authService.performTokenRefresh().pipe(
      mergeMap((newAccessToken: string) => {
        if (newAccessToken) {
          this.logger.info('Token refreshed successfully, retrying request');
          this.broadcaster.broadcast('access_token refreshed');
          const retriedRequest = this.setAuthHeader(request, newAccessToken);
          return next.handle(retriedRequest);
        } else {
          this.logger.info('Token refresh returned empty token');
          throw originalError;
        }
      }),
      catchError((refreshError) => {
        this.logger.error('Token refresh failed:', refreshError);
        // Return the original error, not the refresh error
        throw originalError;
      })
    );
  }
}